import dayjs from 'dayjs';
import { Maybe } from 'graphql/jsutils/Maybe';

import { TrialImplementationStatus } from '@shared/services/gql.service';
import { InvoiceModel } from '@shared/store/invoice/invoice.model';
import { TrialModel } from '@shared/store/trials/trials.store';
import { Utils } from '@shared/utils/utils';

import { InvoiceForm } from './invoice-form.model';

export class InvoiceFormUtils {
  static invoiceFormToInvoiceModel = (formValues: InvoiceForm, invoice: InvoiceModel) => {
    return <InvoiceModel>{
      ...invoice,
      accrual_period: formValues.accrual_period,
      due_date: formValues.due_date,
      expense_amounts: {
        discount_total: {
          ...invoice.expense_amounts.discount_total,
          value: Utils.reverseAccountingFormat(formValues.discount_total),
        },
        discount_total_trial_currency: {
          ...invoice.expense_amounts.discount_total_trial_currency,
          value: Utils.reverseAccountingFormat(formValues.discount_total_trial_currency),
        },
        investigator_total: {
          ...invoice.expense_amounts.investigator_total,
          value: Utils.reverseAccountingFormat(formValues.investigator_total),
        },
        investigator_total_trial_currency: {
          ...invoice.expense_amounts.investigator_total_trial_currency,
          value: Utils.reverseAccountingFormat(formValues.investigator_total_trial_currency),
        },
        invoice_total: {
          ...invoice.expense_amounts.invoice_total,
          value: Utils.reverseAccountingFormat(formValues.invoice_total),
        },
        invoice_total_trial_currency: {
          ...invoice.expense_amounts.invoice_total_trial_currency,
          value: Utils.reverseAccountingFormat(formValues.invoice_total_trial_currency),
        },
        pass_thru_total: {
          ...invoice.expense_amounts.pass_thru_total,
          value: Utils.reverseAccountingFormat(formValues.pass_thru_total),
        },
        pass_thru_total_trial_currency: {
          ...invoice.expense_amounts.pass_thru_total_trial_currency,
          value: Utils.reverseAccountingFormat(formValues.pass_thru_total_trial_currency),
        },
        services_total: {
          ...invoice.expense_amounts.services_total,
          value: Utils.reverseAccountingFormat(formValues.services_total),
        },
        services_total_trial_currency: {
          ...invoice.expense_amounts.services_total_trial_currency,
          value: Utils.reverseAccountingFormat(formValues.services_total_trial_currency),
        },
      },
      invoice_date: formValues.invoice_date,
      invoice_no: formValues.invoice_no,
      notes: formValues.notes ? [{ message: formValues.notes }] : [],
      organization: { id: formValues.vendor_id },
      po_reference: formValues.selectedPOReference,
      services_period: formValues.services_period,
    };
  };

  static invoiceModelToForm = (invoice: InvoiceModel): InvoiceForm => {
    return {
      accrual_period: invoice.accrual_period
        ? dayjs(invoice.accrual_period).format('YYYY-MM')
        : null,
      discount_total: Utils.agCurrencyFormatter(
        invoice.expense_amounts.discount_total,
        invoice.expense_amounts.discount_total.contract_curr || invoice.organization.currency
      ).toString(),
      discount_total_trial_currency: Utils.currencyFormatter(
        invoice.expense_amounts.discount_total_trial_currency.value
      ).toString(),
      due_date: invoice.due_date,
      investigator_total: Utils.agCurrencyFormatter(
        invoice.expense_amounts.investigator_total,
        invoice.expense_amounts.investigator_total.contract_curr || invoice.organization.currency
      ).toString(),
      investigator_total_trial_currency: Utils.currencyFormatter(
        invoice.expense_amounts.investigator_total_trial_currency.value
      ).toString(),
      invoice_date: invoice.invoice_date,
      invoice_no: invoice.invoice_no,
      invoice_total: Utils.agCurrencyFormatter(
        invoice.expense_amounts.invoice_total,
        invoice.expense_amounts.invoice_total.contract_curr || invoice.organization.currency
      ).toString(),
      invoice_total_trial_currency: Utils.currencyFormatter(
        invoice.expense_amounts.invoice_total_trial_currency.value
      ).toString(),
      notes: invoice.notes?.[0]?.message || '',
      pass_thru_total: Utils.agCurrencyFormatter(
        invoice.expense_amounts.pass_thru_total,
        invoice.expense_amounts.pass_thru_total.contract_curr || invoice.organization.currency
      ).toString(),
      pass_thru_total_trial_currency: Utils.currencyFormatter(
        invoice.expense_amounts.pass_thru_total_trial_currency.value
      ).toString(),
      selectedPOReference: invoice.po_reference,
      services_period: invoice.services_period
        ? dayjs(invoice.services_period).format('YYYY-MM')
        : null,
      services_total: Utils.agCurrencyFormatter(
        invoice.expense_amounts.services_total,
        invoice.expense_amounts.services_total.contract_curr || invoice.organization.currency
      ).toString(),
      services_total_trial_currency: Utils.currencyFormatter(
        invoice.expense_amounts.services_total_trial_currency.value
      ).toString(),
      vendor_id: invoice.organization.id,
    };
  };

  static isAccrualPeriodInClosedMonth = ({
    currentOpenMonth,
    invoice,
    trial,
  }: {
    currentOpenMonth: string;
    invoice: InvoiceModel;
    trial: Maybe<TrialModel>;
  }) => {
    if (!trial?.implementation_status) {
      return false;
    }

    if (
      trial.implementation_status !== TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED &&
      trial.implementation_status !== TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE
    ) {
      return false;
    }

    if (invoice.invoice_status === 'STATUS_DECLINED') {
      return false;
    }

    if (!invoice.accrual_period) {
      return false;
    }

    let period = invoice.accrual_period || '';

    if (period.split('-').length === 1) {
      period = `${period}-01`;
    }

    return dayjs(period).isBefore(currentOpenMonth);
  };
}
